import { useState, type FormEvent } from "react";
import type { ProviderKeyInfo } from "../../types/openclaw";
import { PROVIDERS, getProviderLabel } from "./constants";
import styles from "./shared.module.css";

type Props = {
  providers: ProviderKeyInfo[];
  submitting: boolean;
  onAdd: (provider: string, key: string) => Promise<void>;
  onRemove: (provider: string) => void; // triggers confirmation, not direct delete
};

// Hoisted to module scope so the parent re-rendering on each keystroke
// does not unmount/remount the <input> (which would drop IME composition,
// reset cursor position, and close password-manager popups).
type KeyInputProps = {
  provider: typeof PROVIDERS[number];
  keyInput: string;
  setKeyInput: (v: string) => void;
  submitting: boolean;
  onSubmit: (provider: string) => Promise<void> | void;
  onCancel: () => void;
};

const KeyInput = ({ provider, keyInput, setKeyInput, submitting, onSubmit, onCancel }: KeyInputProps): JSX.Element => (
  <form className={styles.keyForm} onSubmit={(e: FormEvent) => { e.preventDefault(); void onSubmit(provider.id); }}>
    <input
      type="text"
      className={styles.keyInput}
      style={{ WebkitTextSecurity: "disc" } as React.CSSProperties}
      value={keyInput}
      onChange={(e) => setKeyInput(e.target.value)}
      placeholder={provider.placeholder}
      autoFocus
      disabled={submitting}
      autoComplete="off"
      data-1p-ignore="true"
      data-lpignore="true"
    />
    <button type="submit" className={styles.primaryBtnSm} disabled={submitting || !keyInput.trim()}>
      {submitting ? "..." : "Save"}
    </button>
    <button type="button" className={styles.secondaryBtnSm} onClick={onCancel} disabled={submitting}>
      Cancel
    </button>
  </form>
);

export const ProviderKeyList = ({ providers, submitting, onAdd, onRemove }: Props): JSX.Element => {
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [addingProvider, setAddingProvider] = useState<string | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const [showMore, setShowMore] = useState(false);

  const configuredKeys = providers.filter((p) => p.configured);
  const configuredIds = new Set(configuredKeys.map((p) => p.provider));

  const handleSubmit = async (provider: string): Promise<void> => {
    if (!keyInput.trim()) return;
    await onAdd(provider, keyInput.trim());
    setKeyInput("");
    setEditingProvider(null);
    setAddingProvider(null);
  };

  return (
    <div>
      {configuredKeys.map((k) => {
        const prov = PROVIDERS.find((p) => p.id === k.provider);
        const isEditing = editingProvider === k.provider;
        return (
          <div key={k.provider} className={styles.keyRow}>
            <span className={styles.keyProvider}>{getProviderLabel(k.provider)}</span>
            {isEditing ? (
              <KeyInput
                provider={prov ?? PROVIDERS[0]}
                keyInput={keyInput}
                setKeyInput={setKeyInput}
                submitting={submitting}
                onSubmit={handleSubmit}
                onCancel={() => { setEditingProvider(null); setKeyInput(""); }}
              />
            ) : (
              <>
                <code className={styles.maskedKey}>{k.maskedKey ?? "****"}</code>
                <span className={styles.keyActions}>
                  <button type="button" className={styles.link} disabled={submitting}
                    onClick={() => { setEditingProvider(k.provider); setKeyInput(""); }}>
                    Update
                  </button>
                  <button type="button" className={styles.dangerLink} disabled={submitting}
                    onClick={() => onRemove(k.provider)}>
                    Remove
                  </button>
                </span>
              </>
            )}
          </div>
        );
      })}

      {addingProvider ? (
        <div className={styles.keyRow}>
          <span className={styles.keyProvider}>{getProviderLabel(addingProvider)}</span>
          <KeyInput
            provider={PROVIDERS.find((p) => p.id === addingProvider)!}
            keyInput={keyInput}
            setKeyInput={setKeyInput}
            submitting={submitting}
            onSubmit={handleSubmit}
            onCancel={() => { setAddingProvider(null); setKeyInput(""); }}
          />
        </div>
      ) : (
        <>
          {!showMore ? (
            <button type="button" className={styles.addKeyBtn}
              onClick={() => setShowMore(true)}>
              + Add {configuredKeys.length > 0 ? "another" : "a"} key
            </button>
          ) : (
            <div className={styles.altProviders}>
              {configuredKeys.length > 0 && (
                <span className={styles.altProvidersTitle}>Add key for</span>
              )}
              {PROVIDERS.filter((p) => !configuredIds.has(p.id)).map((p) => (
                <button key={p.id} type="button" className={styles.altProviderBtn}
                  onClick={() => { setAddingProvider(p.id); setKeyInput(""); setShowMore(false); }}>
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
